import {
    STORE_SEARCH_FILTER_TYPE_BRAND,
    STORE_SEARCH_FILTER_TYPE_CATEGORY, STORE_SEARCH_FILTER_TYPE_NONE,
    StoreSearchFilterModal
} from "components/modal/search/StoreSearchFilterModal";
import styles from "components/sections/search/store/StoreSearchResult.module.scss";
import SearchQueryTextBox from "components/textbox/SearchQueryTextBox";
import {useEffect, useMemo, useRef, useState} from "react";
import {Axios} from "api";
import {captureException, captureMessage} from "@sentry/nextjs";
import {useRouter} from "next/router";
import ArrowDownIcon from "images/icons/arrow_bottom.svg";
import useTranslation from 'next-translate/useTranslation';


const FilterText = (props) => {
    const {name} = props;
    return (
        <span className={styles.filterText}>
            <span>{name}</span>
            <ArrowDownIcon viewBox="0 0 20 20" className={styles.downIcon}/>
        </span>
    )
}

export const STORE_SEARCH_FILTER_TYPE_SPECIAL_PRICE = 'special_price';
export const STORE_SEARCH_FILTER_TYPE_NAME_ONLY = 'name_only';


export const StoreSearchFilter = (props) => {
    const {t} = useTranslation('sections-search-store-StoreSearchFilter');
    const {setError, queryFilterInfo} = props;

    const [modalMode, setModalMode] = useState(STORE_SEARCH_FILTER_TYPE_NONE);

    const [filterInfo, setFilterInfo] = useState({
        tag_relations: [],
        brands: [],
        total_count: 0,
    });

    const [allFilterInfo, setAllFilterInfo] = useState(null);

    const router = useRouter();

    const query = router.query.q || '';
    const sortOrder = router.query.order;
    const isNameOnly = router.query[STORE_SEARCH_FILTER_TYPE_NAME_ONLY] === 'true';
    const isSpecialPriceOnly = router.query[STORE_SEARCH_FILTER_TYPE_SPECIAL_PRICE] === 'true';
    const categoryIds = router.query.categories ? router.query.categories.split(',').map(x => parseInt(x)) : [];
    const brandIds = router.query.brands ? router.query.brands.split(',').map(x => parseInt(x)) : [];

    const currentFilterInfo = {
        isNameOnly,
        isSpecialPriceOnly,
        categoryIds,
        brandIds
    };

    useEffect(() => {
        const {query, isNameOnly, isSpecialPriceOnly, categoryIds, brandIds} = queryFilterInfo;
        fetchFilters(query, isNameOnly, isSpecialPriceOnly, categoryIds, brandIds);
    }, [query, isNameOnly, isSpecialPriceOnly]);

    const fetchFiltersRef = useRef(0);

    const fetchFilters = async (query, isNameOnly, isSpecialPriceOnly, categoryIds, brandIds) => {
        try {
            setError(false);

            fetchFiltersRef.current += 1;
            const lastId = fetchFiltersRef.current;
            console.log('fetchFilters', lastId, query, categoryIds, brandIds);
            const res = await Axios.post(
                `v1/search/store-filters`,
                {
                    query,
                    is_name_only: isNameOnly,
                    is_special_price_only: isSpecialPriceOnly,
                    tag_ids: categoryIds,
                    brand_ids: brandIds,
                }
            );
            if (res.status < 400) {
                console.log('fetchFilters res', res);
                if (lastId !== fetchFiltersRef.current) {
                    return;
                }
                setFilterInfo(res.data);
                // 최초에만 allFilterInfo를 설정한다.
                if (allFilterInfo === null) {
                    setAllFilterInfo({
                        tag_relations: res.data.all_tag_relations,
                        brands: res.data.all_brands,
                    });
                }
            } else {
                captureMessage(JSON.stringify(res.data));
                setError(true);
            }
        } catch (e) {
            console.log('fetchProducts error', e);
            setError(true);
            captureException(e);
        }
    };

    const idToCategoryFilter = useMemo(() => {
        console.log('allFilterInfo', allFilterInfo);
        if (!Boolean(allFilterInfo)) {
            return {};
        }
        const idToFilter = {};
        if (!Boolean(allFilterInfo.tag_relations)) {
            return idToFilter;
        }
        for (const relationList of allFilterInfo.tag_relations) {
            for (const tagFilterInfo of relationList.child_tags) {
                idToFilter[tagFilterInfo.tag.id] = tagFilterInfo
            }
        }
        return idToFilter;
    }, [allFilterInfo]);

    const idToBrandFilter = useMemo(() => {
        if (!Boolean(allFilterInfo)) {
            return {};
        }
        return Object.assign({}, ...allFilterInfo.brands.map(brandFilter => {
                    return {[brandFilter.brand.id]: brandFilter}
                }
            )
        )
    }, [allFilterInfo]);

    const handleSubmit = (currentFilterInfo) => {
        router.replace({
            query: {
                ...router.query,
                brands: currentFilterInfo.brandIds.join(','),
                categories: currentFilterInfo.categoryIds.join(',')
            }
        });
        setModalMode(STORE_SEARCH_FILTER_TYPE_NONE);
    };

    const handleCloseModal = () => {
        setModalMode(STORE_SEARCH_FILTER_TYPE_NONE);
    };

    const openModal = (mode) => {
        typeof mixpanel !== 'undefined' && mixpanel.track('StoreSearchFilterModalOpen', {mode});
        setModalMode(mode);
    };

    const categoryFilterName = filterInfo && categoryIds && categoryIds.length > 0 && idToCategoryFilter[categoryIds[0]]?.tag?.name;
    const categoryFilterFullName = categoryFilterName ?
        (categoryIds.length > 1 ?
            `${categoryFilterName} ${t('FilterText.others', { count: categoryIds.length - 1 })}`
            : categoryFilterName)
        : t('FilterText.category');
    const brandFilterName = filterInfo && brandIds && brandIds.length > 0 && idToBrandFilter[brandIds[0]]?.brand?.name;
    const brandFilterFullName = brandFilterName ?
        (brandIds.length > 1 ?
            `${brandFilterName} ${t('FilterText.others', { count: brandIds.length - 1 })}`
            : brandFilterName)
        : t('FilterText.brand');

    const handleToggleBooleanFilter = (filterType) => {
        typeof mixpanel !== 'undefined' && mixpanel.track(
            'StoreSearchFilterToggleFilter',
            {
                filterType,
            }
        );
        router.replace({
            query: {
                ...router.query,
                [filterType]: router.query[filterType] === 'true' ? 'false' : 'true'
            }
        });
    };

    return (
        <>
            <StoreSearchFilterModal
                filterInfo={filterInfo}
                idToCategoryFilter={idToCategoryFilter}
                idToBrandFilter={idToBrandFilter}
                currentFilterInfo={currentFilterInfo}
                handleSubmit={handleSubmit}
                handleClose={handleCloseModal}
                modalMode={modalMode}
                queryFilterInfo={queryFilterInfo}
                fetchFilters={fetchFilters}
            />
            <div className={styles.filterContainer}>
                <SearchQueryTextBox
                    key={'store-search-filter-special-price'}
                    query={t('StoreSearchFilter.specialPrice')}
                    isSelected={isSpecialPriceOnly}
                    onClick={() => {handleToggleBooleanFilter(STORE_SEARCH_FILTER_TYPE_SPECIAL_PRICE)}}
                />
                <SearchQueryTextBox
                    key={'store-search-filter-name-only'}
                    query={t('StoreSearchFilter.nameOnly')}
                    isSelected={isNameOnly}
                    onClick={() => {handleToggleBooleanFilter(STORE_SEARCH_FILTER_TYPE_NAME_ONLY)}}
                />
                <SearchQueryTextBox
                    key={'store-search-filter-category'}
                    query={<FilterText name={categoryFilterFullName}/>}
                    isSelected={categoryFilterFullName !== t('FilterText.category')}
                    onClick={() => {
                        openModal(STORE_SEARCH_FILTER_TYPE_CATEGORY)
                    }}
                />
                <SearchQueryTextBox
                    key={'store-search-filter-brand'}
                    query={<FilterText name={brandFilterFullName}/>}
                    isSelected={brandFilterFullName !== t('FilterText.brand')}
                    onClick={() => {
                        openModal(STORE_SEARCH_FILTER_TYPE_BRAND)
                    }}
                />
            </div>
        </>
    )

};
